import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ApiError,
  createSchedulesBulk,
  listProducts,
  type BulkScheduleItem,
} from '../lib/api-client';
import { schedulableProducts, SCHEDULABLE_HINT } from '../lib/schedulable-products';

/**
 * 排班（AC-A2-1 / AC-A2-2 重設計）。
 *
 * 取代舊「新增班次」+「批量建立」兩個分裂頁面，合併為單一直覺流程：
 *   ① 選商品
 *   ② 時段（可多列：出發時間 + 時長）—— 解決「多時段要跑多次批量」
 *   ③ 日期：單日 / 日期範圍
 *   ④ 範圍模式下「重複於」星期（快捷：每天/平日/週末）—— 取代抽象 interval_days
 *   ⑤ 容量 / 最少出航人數
 *   ⑥ 即時預覽（所有 日期×時段 組合 + 總數 + 上限提示）
 *   ⑦ 一鍵建立（後端 POST /schedules/bulk：原子交易 + 對既有 departure_at 去重）
 *
 * 編輯既有班次（改容量 / 天候停航）仍走 ScheduleEditPage（`/schedules/:id`）。
 */

const MAX_ITEMS = 500; // 與後端 CreateSchedulesBulkDto ArrayMaxSize 對齊
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六']; // index = getDay()

interface Slot {
  time: string; // HH:mm
  durationMin: number;
}

/** date(YYYY-MM-DD) + time(HH:mm) → ISO（本地時區）；addMin 用於推 departure_end_at。 */
function toIso(dateStr: string, timeStr: string, addMin = 0): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  const dt = new Date(y!, mo! - 1, d!, h!, mi!, 0, 0);
  if (addMin) dt.setMinutes(dt.getMinutes() + addMin);
  return dt.toISOString();
}

/** 列出 start..end（含端點）之每一天 YYYY-MM-DD；start>end 回空。 */
function enumerateDates(startDate: string, endDate: string): string[] {
  if (startDate === '' || endDate === '') return [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const cur = new Date(sy!, sm! - 1, sd!);
  const end = new Date(ey!, em! - 1, ed!);
  if (cur.getTime() > end.getTime()) return [];
  const out: string[] = [];
  const pad = (n: number): string => String(n).padStart(2, '0');
  while (cur.getTime() <= end.getTime() && out.length <= 400) {
    out.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function SchedulePlanPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const products = useQuery({
    queryKey: ['products.list'],
    queryFn: () => listProducts({ limit: 200 }),
  });
  const productOptions = useMemo(() => products.data?.items ?? [], [products.data]);

  const [productId, setProductId] = useState('');
  const [dateMode, setDateMode] = useState<'single' | 'range'>('single');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekdays, setWeekdays] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5, 6]));
  const [slots, setSlots] = useState<Slot[]>([{ time: '09:00', durationMin: 120 }]);
  const [capacity, setCapacity] = useState<number>(50);
  const [minPassengers, setMinPassengers] = useState<number>(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (items: BulkScheduleItem[]) =>
      createSchedulesBulk({ product_id: productId, items }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ['schedules.list'] });
      const msg =
        `已建立 ${resp.created_count} 筆班次` +
        (resp.skipped_count > 0 ? `；略過 ${resp.skipped_count} 筆（已存在相同出發時間）` : '');
      // 以 query param 帶結果訊息回列表（列表頁可顯示 toast；此處最小化先 alert 後導頁）
      window.setTimeout(() => {
        navigate(`/schedules?product_id=${encodeURIComponent(productId)}`);
      }, 0);
      window.alert(msg);
    },
  });

  // ── 即時預覽：算出所有 (日期×時段) 組合 ──
  const dates = useMemo<string[]>(() => {
    if (dateMode === 'single') return startDate === '' ? [] : [startDate];
    return enumerateDates(startDate, endDate).filter((d) => {
      const [y, mo, dd] = d.split('-').map(Number);
      return weekdays.has(new Date(y!, mo! - 1, dd!).getDay());
    });
  }, [dateMode, startDate, endDate, weekdays]);

  const previewItems = useMemo<BulkScheduleItem[]>(() => {
    const items: BulkScheduleItem[] = [];
    for (const d of dates) {
      for (const s of slots) {
        if (!/^\d{2}:\d{2}$/.test(s.time)) continue;
        items.push({
          departure_at: toIso(d, s.time),
          departure_end_at: toIso(d, s.time, s.durationMin),
          capacity,
          min_passengers: minPassengers,
        });
      }
    }
    return items;
  }, [dates, slots, capacity, minPassengers]);

  const toggleWeekday = (w: number): void => {
    setWeekdays((prev) => {
      const next = new Set(prev);
      if (next.has(w)) next.delete(w);
      else next.add(w);
      return next;
    });
  };

  const validate = (): string | null => {
    if (productId === '') return '請選擇商品';
    if (startDate === '') return '請選擇日期';
    if (dateMode === 'range' && endDate === '') return '請選擇結束日期';
    if (dateMode === 'range' && weekdays.size === 0) return '請至少選一個星期';
    if (slots.length === 0) return '請至少新增一個時段';
    for (const s of slots) {
      if (!/^\d{2}:\d{2}$/.test(s.time)) return '時段時間格式需為 HH:mm';
      if (!Number.isInteger(s.durationMin) || s.durationMin < 1 || s.durationMin > 1440)
        return '時長需為 1..1440 分鐘';
    }
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 500) return '容量需為 1..500';
    if (minPassengers < 0 || minPassengers > capacity) return '最少出航人數需介於 0 與容量之間';
    if (previewItems.length === 0) return '依目前條件不會產生任何班次（檢查日期 / 星期 / 時段）';
    if (previewItems.length > MAX_ITEMS)
      return `一次最多建立 ${MAX_ITEMS} 筆，目前為 ${previewItems.length} 筆`;
    return null;
  };

  const onSubmit = (): void => {
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);
    mut.mutate(previewItems);
  };

  const previewByDate = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const d of dates) {
      map.set(
        d,
        slots.filter((s) => /^\d{2}:\d{2}$/.test(s.time)).map((s) => s.time),
      );
    }
    return [...map.entries()];
  }, [dates, slots]);

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">排班（AC-A2-1 / A2-2）</h1>
        <div className="page-head__actions">
          <button onClick={() => navigate('/schedules')}>返回列表</button>
        </div>
      </div>
      <p className="page-intro" style={{ color: '#6b7280' }}>
        選商品、加時段、選日期（單日或範圍），即可一次建立多時段、多天班次；右側即時預覽將建立的班次。
      </p>

      <div style={{ display: 'flex', gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
        {/* ── 左：表單 ── */}
        <div style={{ flex: '1 1 460px', maxWidth: 560 }}>
          <Field label="商品">
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={products.isLoading}
              style={{ width: '100%' }}
            >
              <option value="">— 請選擇 —</option>
              {schedulableProducts(productOptions).map((p) => (
                <option key={p.id} value={p.id}>
                  [{p.sku}] {p.name_i18n['zh-TW'] ?? p.name_i18n['en'] ?? '(未命名)'}
                </option>
              ))}
            </select>
            <small style={{ color: '#6b7280' }}>{SCHEDULABLE_HINT}</small>
          </Field>

          {/* 時段（多列） */}
          <Field label="時段（出發時間 + 時長分鐘）">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {slots.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="time"
                    value={s.time}
                    onChange={(e) =>
                      setSlots((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)),
                      )
                    }
                    style={{ flex: '0 0 130px' }}
                  />
                  <input
                    type="number"
                    value={s.durationMin}
                    min={1}
                    max={1440}
                    onChange={(e) =>
                      setSlots((prev) =>
                        prev.map((x, j) =>
                          j === i
                            ? { ...x, durationMin: Number.parseInt(e.target.value, 10) || 0 }
                            : x,
                        ),
                      )
                    }
                    style={{ flex: '0 0 90px' }}
                  />
                  <span style={{ fontSize: 13, color: '#6b7280' }}>分</span>
                  <button
                    type="button"
                    onClick={() => setSlots((prev) => prev.filter((_, j) => j !== i))}
                    disabled={slots.length <= 1}
                    style={{ marginLeft: 'auto' }}
                  >
                    移除
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setSlots((prev) => [...prev, { time: '14:00', durationMin: 120 }])}
              >
                + 新增時段
              </button>
            </div>
          </Field>

          {/* 日期模式 */}
          <Field label="日期">
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="radio"
                  checked={dateMode === 'single'}
                  onChange={() => setDateMode('single')}
                />
                單日
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="radio"
                  checked={dateMode === 'range'}
                  onChange={() => setDateMode('range')}
                />
                日期範圍
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ flex: 1 }}
              />
              {dateMode === 'range' && (
                <>
                  <span style={{ color: '#6b7280' }}>～</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ flex: 1 }}
                  />
                </>
              )}
            </div>
          </Field>

          {/* 重複於（星期）—— 僅範圍模式 */}
          {dateMode === 'range' && (
            <Field label="重複於">
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button type="button" onClick={() => setWeekdays(new Set([0, 1, 2, 3, 4, 5, 6]))}>
                  每天
                </button>
                <button type="button" onClick={() => setWeekdays(new Set([1, 2, 3, 4, 5]))}>
                  平日
                </button>
                <button type="button" onClick={() => setWeekdays(new Set([0, 6]))}>
                  週末
                </button>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {WEEKDAY_LABELS.map((label, w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => toggleWeekday(w)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: '1px solid #d1d5db',
                      background: weekdays.has(w) ? '#2563eb' : '#fff',
                      color: weekdays.has(w) ? '#fff' : '#374151',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <Field label="容量（1..500）">
              <input
                type="number"
                value={capacity}
                min={1}
                max={500}
                onChange={(e) => setCapacity(Number.parseInt(e.target.value, 10) || 0)}
                style={{ width: 120 }}
              />
            </Field>
            <Field label="最少出航人數">
              <input
                type="number"
                value={minPassengers}
                min={0}
                onChange={(e) => setMinPassengers(Number.parseInt(e.target.value, 10) || 0)}
                style={{ width: 120 }}
              />
            </Field>
          </div>

          {submitError && <div className="error-banner">{submitError}</div>}
          {mut.error && (
            <div className="error-banner">
              {mut.error instanceof ApiError ? mut.error.message : (mut.error as Error).message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="primary"
              onClick={onSubmit}
              disabled={mut.isPending || previewItems.length === 0}
            >
              {mut.isPending ? '建立中…' : `建立 ${previewItems.length} 筆班次`}
            </button>
          </div>
        </div>

        {/* ── 右：即時預覽 ── */}
        <div
          style={{
            flex: '1 1 320px',
            maxWidth: 400,
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 16,
            alignSelf: 'flex-start',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            預覽：共 {previewItems.length} 筆
            {previewItems.length > MAX_ITEMS && (
              <span style={{ color: '#dc2626' }}>（超過上限 {MAX_ITEMS}）</span>
            )}
          </div>
          {dateMode === 'range' && (
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              {dates.length} 天 × {slots.length} 時段
            </div>
          )}
          <div style={{ maxHeight: 420, overflowY: 'auto', fontSize: 13 }}>
            {previewByDate.length === 0 && (
              <div style={{ color: '#9ca3af' }}>選擇日期與時段後顯示預覽</div>
            )}
            {previewByDate.slice(0, 60).map(([d, times]) => (
              <div key={d} style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{d}</span>{' '}
                <span style={{ color: '#374151' }}>{times.join('、')}</span>
              </div>
            ))}
            {previewByDate.length > 60 && (
              <div style={{ color: '#9ca3af' }}>…還有 {previewByDate.length - 60} 天</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
